import { formatDateOnlyLocal, parseDateOnlyLocal } from '@erp/shared-utils';

// Date plumbing shared by the procurement override forms (lead-time overrides,
// inventory-policy overrides, growth factors). The backend stores instants with
// a half-open [startsAt, endsAt) window. Day-grain forms work in LOCAL calendar
// days through `<input type="date">` (YYYY-MM-DD); the month-grain growth
// factor form works in UTC months through `<input type="month">` (YYYY-MM).
// Everything that crosses either boundary goes through here so the forms agree
// on what "ends on D" and "through month M" mean.
//
// The local calendar-day conversions themselves are `@erp/shared-utils/date`;
// this module only adds the window semantics on top.

/**
 * How precisely a window's bounds mean anything. Lead-time and inventory-policy
 * overrides are entered and stored as local calendar days. A growth factor's
 * bounds are UTC month boundaries (the table CHECKs it) because it scales whole
 * monthly forecast buckets, so naming a day there implies a precision that does
 * not exist.
 */
export type OverrideWindowGrain = 'day' | 'month';

// ─── Day grain ──────────────────────────────────────────────────────────────

/** Today plus N months, as a date-input value: the default override horizon. */
export function localDateInMonths(months: number): string {
  const d = new Date();
  d.setMonth(d.getMonth() + months);
  return formatDateOnlyLocal(d);
}

// Parse a `YYYY-MM-DD` (from the date input) as a LOCAL calendar day, not UTC.
// `new Date('2026-12-08')` would be UTC midnight, which renders/expires a day
// early in negative-offset timezones. A date input only ever hands us '' or a
// real calendar date, so anything else falls through as an Invalid Date rather
// than throwing inside a render.
export function parseLocalDate(s: string): Date {
  return parseDateOnlyLocal(s) ?? new Date(NaN);
}

// The backend window is half-open `[startsAt, endsAt)`. A user picking end-day D
// means "effective through D", so the exclusive end is the next local midnight.
export function toExclusiveEndIso(s: string): string {
  const d = parseLocalDate(s);
  d.setDate(d.getDate() + 1);
  return d.toISOString();
}

/** The inverse of {@link toExclusiveEndIso}: the inclusive day the user picked. */
export function exclusiveEndIsoToDateInput(iso: string): string {
  const d = new Date(iso);
  d.setDate(d.getDate() - 1);
  return formatDateOnlyLocal(d);
}

// The window's start is INCLUSIVE, so day D means "from the morning of D":
// the start of that local day. Same conversion as toReviewAtIso, kept separate
// because the two would diverge if either end's semantics ever changed.
export function toStartsAtIso(s: string): string {
  return parseLocalDate(s).toISOString();
}

// A review date is advisory ("look at this again by D"), so it is the start of
// that local day: the row shows as needing review from the morning of D.
export function toReviewAtIso(s: string): string {
  return parseLocalDate(s).toISOString();
}

export function isoToLocalDateInput(iso: string): string {
  return formatDateOnlyLocal(new Date(iso));
}

// ─── Month grain ────────────────────────────────────────────────────────────
// A growth factor covers whole forecast months and is stored on UTC month
// boundaries, so its bounds convert straight from the month picked to
// `Date.UTC(...)` and back through the UTC getters. Routing them through local
// midnight, as the day-grain helpers do, lands a few hours off the boundary in
// every timezone but UTC, and the server's floor/ceil snap then moves the bound
// by a whole month: a Toronto buyer's "through December" became "through
// January", and each edit dragged the start back a month.

const MONTH_INPUT = /^(\d{4})-(\d{2})$/;

/**
 * A `YYYY-MM` month-input value as its parts, or null for '' and anything
 * malformed. A browser without `type="month"` falls back to a text field, so
 * the form has to cope with what a buyer types there.
 */
export function parseMonthInput(
  s: string,
): { year: number; month: number } | null {
  const m = MONTH_INPUT.exec(s);
  if (!m) return null;
  const month = Number(m[2]);
  if (month < 1 || month > 12) return null;
  return { year: Number(m[1]), month };
}

/** First instant (UTC) of the given month; `monthIndex` is 0-based and may overflow into the next year. */
function utcMonthStart(year: number, monthIndex: number): Date {
  return new Date(Date.UTC(year, monthIndex, 1));
}

function utcMonthInput(d: Date): string {
  const year = String(d.getUTCFullYear()).padStart(4, '0');
  const month = String(d.getUTCMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
}

/** The first instant of the picked month: the inclusive start. Null when malformed. */
export function monthInputToStartsAtIso(s: string): string | null {
  const p = parseMonthInput(s);
  return p ? utcMonthStart(p.year, p.month - 1).toISOString() : null;
}

/**
 * The picked month is the LAST one covered, so the exclusive end is the first
 * instant of the month after it. Null when malformed.
 */
export function monthInputToExclusiveEndIso(s: string): string | null {
  const p = parseMonthInput(s);
  return p ? utcMonthStart(p.year, p.month).toISOString() : null;
}

/** The UTC month an instant falls in, as a month-input value. */
export function isoToUtcMonthInput(iso: string): string {
  return utcMonthInput(new Date(iso));
}

/**
 * The inverse of {@link monthInputToExclusiveEndIso}: the last month the
 * window covers. Stepping back a millisecond from the exclusive bound lands in
 * that month whether or not the stored bound sits on a boundary.
 */
export function exclusiveEndIsoToMonthInput(iso: string): string {
  return utcMonthInput(new Date(new Date(iso).getTime() - 1));
}

/** The current UTC month plus N, as a month-input value: the default month-grain horizon. */
export function utcMonthInMonths(months: number): string {
  const now = new Date();
  return utcMonthInput(
    utcMonthStart(now.getUTCFullYear(), now.getUTCMonth() + months),
  );
}

// ─── Both grains: the bounds as the API takes them ──────────────────────────

export interface ResolvedWindowBounds {
  /** Omitted when blank: the server defaults it to now. */
  startsAt: string | undefined;
  /** Exclusive; null for open-ended. */
  endsAt: string | null;
  /** A bound was entered but does not parse: only possible at month grain, in a browser without `type="month"`. */
  malformed: boolean;
}

/** Calendar-day inputs (a date input only ever yields '' or a real date). */
export function dayBounds(
  startsAt: string,
  endsAt: string,
): ResolvedWindowBounds {
  return {
    startsAt: startsAt ? toStartsAtIso(startsAt) : undefined,
    endsAt: endsAt ? toExclusiveEndIso(endsAt) : null,
    malformed: false,
  };
}

/** Month inputs; the end month is the last one covered. */
export function monthBounds(
  startsAt: string,
  endsAt: string,
): ResolvedWindowBounds {
  const start = startsAt ? monthInputToStartsAtIso(startsAt) : undefined;
  const end = endsAt ? monthInputToExclusiveEndIso(endsAt) : null;
  return {
    startsAt: start ?? undefined,
    endsAt: end ?? null,
    malformed: start === null || (endsAt !== '' && end === null),
  };
}
